import { Button, Form, Input, Spin, type FormListFieldData } from "antd";
import {
  ArrowLeft,
  CalendarClock,
  Check,
  CircleCheck,
  ClipboardList,
  FileQuestion,
  FileText,
  MessageSquareText,
  UploadCloud,
} from "lucide-react";
import { useEffect, useRef, useState, type DragEvent } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import {
  useGetAssessmentInterviewQuery,
  useSubmitAssessmentMutation,
} from "../../app/features/recruitment/recruitment.api";
import useAuth from "../../hooks/useAuth";
import WebLayout from "../../layouts/WebLayout";
import cn from "../../lib/cn";
import { toast } from "../../lib/toast";
import { formatDate, formatTimeString } from "../../utils/date";
import { getFileNameFromUrl } from "../../utils/upload";
import { EmptyState, panelClass } from "./components/portal";
import useFileUpload from "./components/useFileUpload";
import { PATHS } from "../../routes/paths";

/** Passed from the applied job card; the assessment API has no job info. */
type JobContext = { job_title?: string; organization_name?: string };

type AnswerValue = {
  question_id: string;
  question_type: "mcq" | "text" | "attachment";
  title: string;
  mark: string;
  options: { id: number; value: string }[] | null;
  answer_text: string;
  answer_attachment: string[];
  obtained_mark: number | null | undefined;
  evaluator_comment: string | null | undefined;
};

const isAnswered = (answer?: AnswerValue) =>
  answer?.question_type === "attachment"
    ? Boolean(answer.answer_attachment?.[0])
    : Boolean(answer?.answer_text?.trim());

/* =============================== Inputs ================================ */

/** Option list for multiple-choice answers (value = option text). */
const ChoiceList = ({
  options,
  value,
  onChange,
  disabled,
}: {
  options: { id: number; value: string }[];
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
}) => (
  <div role="radiogroup" className="space-y-2">
    {options.map((option, index) => {
      const checked = value === option.value;
      return (
        <button
          key={option.id}
          type="button"
          role="radio"
          aria-checked={checked}
          disabled={disabled}
          onClick={() => onChange?.(option.value)}
          className={cn(
            "flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left text-[15px] transition-colors",
            checked
              ? "border-theme-primary bg-theme-primary/[0.05] text-gray-900"
              : "border-gray-200 text-gray-700",
            !disabled && !checked && "hover:border-gray-300 hover:bg-gray-50",
            disabled && "cursor-default",
          )}
        >
          <span
            className={cn(
              "flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-semibold",
              checked
                ? "bg-theme-primary text-white"
                : "bg-gray-100 text-gray-500",
            )}
          >
            {String.fromCharCode(65 + index)}
          </span>
          {option.value}
        </button>
      );
    })}
  </div>
);

/** Single-file upload; value is a one-item URL array like FileUploader's. */
const AttachmentInput = ({
  value,
  onChange,
  disabled,
}: {
  value?: string[];
  onChange?: (value: string[]) => void;
  disabled?: boolean;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { upload, uploading } = useFileUpload(10);
  const [dragging, setDragging] = useState(false);
  const file = value?.[0];

  const handleFile = async (picked?: File) => {
    if (!picked) return;
    const url = await upload(picked);
    if (url) onChange?.([url]);
    if (inputRef.current) inputRef.current.value = "";
  };

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (!disabled) handleFile(e.dataTransfer.files?.[0]);
  };

  return (
    <>
      {file ? (
        <div className="flex items-center gap-3 rounded-xl border border-gray-200 p-3">
          <span className="flex h-12 w-10 shrink-0 items-center justify-center rounded-lg bg-theme-primary/10 text-theme-primary">
            <FileText className="h-5 w-5" strokeWidth={1.75} />
          </span>
          <div className="min-w-0 flex-1">
            <a
              href={file}
              target="_blank"
              rel="noreferrer"
              className="block truncate text-sm font-medium text-gray-900 hover:text-theme-primary"
            >
              {getFileNameFromUrl(file)}
            </a>
            {!disabled && (
              <div className="mt-1 flex gap-3 text-[13px]">
                <button
                  type="button"
                  disabled={uploading}
                  onClick={() => inputRef.current?.click()}
                  className="font-medium text-theme-primary hover:underline disabled:opacity-60"
                >
                  {uploading ? "Uploading…" : "Replace"}
                </button>
                {!uploading && (
                  <button
                    type="button"
                    onClick={() => onChange?.([])}
                    className="font-medium text-gray-500 hover:text-rose-500"
                  >
                    Remove
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        <button
          type="button"
          disabled={disabled || uploading}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={cn(
            "flex w-full flex-col items-center rounded-xl border border-dashed px-4 py-8 text-center transition-colors",
            dragging
              ? "border-theme-primary bg-theme-primary/[0.04]"
              : "border-gray-300 hover:border-theme-primary/60 hover:bg-gray-50/70",
          )}
        >
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-500">
            {uploading ? (
              <Spin size="small" />
            ) : (
              <UploadCloud className="h-5 w-5" strokeWidth={1.75} />
            )}
          </span>
          <span className="mt-3 text-sm font-medium text-gray-900">
            {uploading ? (
              "Uploading…"
            ) : (
              <>
                <span className="text-theme-primary">Click to upload</span> or
                drag & drop
              </>
            )}
          </span>
          <span className="mt-1 text-xs text-gray-400">
            PDF, DOC or DOCX, up to 10 MB
          </span>
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.doc,.docx"
        className="!hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
    </>
  );
};

/* ============================== Question =============================== */

const Question = ({
  field,
  index,
  total,
  readOnly,
}: {
  field: FormListFieldData;
  index: number;
  total: number;
  readOnly: boolean;
}) => {
  const form = Form.useFormInstance();
  const answer: AnswerValue | undefined = Form.useWatch(
    ["answers", field.name],
    form,
  );
  if (!answer) return null;

  const answered = isAnswered(answer);
  const hasMark =
    answer.obtained_mark !== null && answer.obtained_mark !== undefined;

  return (
    <section
      id={`question-${index + 1}`}
      className={cn(panelClass, "scroll-mt-24 p-5 sm:p-7")}
    >
      <Form.Item name={[field.name, "question_id"]} hidden />
      <Form.Item name={[field.name, "question_type"]} hidden />

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs font-medium text-gray-500">
          <span className="font-semibold text-gray-900">
            Question {index + 1}
          </span>
          <span className="text-gray-300">/ {total}</span>
        </div>
        <span className="rounded-full bg-theme-primary/10 px-2.5 py-0.5 text-xs font-semibold text-theme-primary">
          {hasMark
            ? `${answer.obtained_mark} / ${answer.mark || 0} marks`
            : `${answer.mark || 0} mark${Number(answer.mark) === 1 ? "" : "s"}`}
        </span>
      </div>

      <h2 className="mt-3 text-base font-semibold leading-7 text-gray-900 sm:text-[17px]">
        {answer.title}
      </h2>

      <div className="mt-5">
        {answer.question_type === "mcq" && (
          <Form.Item
            name={[field.name, "answer_text"]}
            rules={[{ required: true, message: "Please select an option" }]}
          >
            <ChoiceList options={answer.options ?? []} disabled={readOnly} />
          </Form.Item>
        )}
        {answer.question_type === "text" && (
          <Form.Item
            name={[field.name, "answer_text"]}
            rules={[{ required: true, message: "Please provide an answer" }]}
          >
            <Input.TextArea
              autoSize={{ minRows: 5, maxRows: 14 }}
              placeholder="Type your answer here…"
              disabled={readOnly}
              className="rounded-xl !px-4 !py-3 text-[15px]"
            />
          </Form.Item>
        )}
        {answer.question_type === "attachment" && (
          <Form.Item
            name={[field.name, "answer_attachment"]}
            rules={[{ required: true, message: "Please upload a file" }]}
          >
            <AttachmentInput disabled={readOnly} />
          </Form.Item>
        )}
      </div>

      {answer.evaluator_comment && (
        <div className="mt-5 flex gap-3 rounded-xl border border-theme-primary/15 bg-theme-primary/[0.04] p-4">
          <MessageSquareText
            className="mt-0.5 h-4 w-4 shrink-0 text-theme-primary"
            strokeWidth={1.75}
          />
          <div>
            <p className="text-xs font-semibold text-gray-900">
              Evaluator comment
            </p>
            <p className="mt-1 text-sm leading-6 text-gray-600">
              {answer.evaluator_comment}
            </p>
          </div>
        </div>
      )}

      {!readOnly && answered && (
        <p className="mt-4 flex items-center gap-1.5 text-xs font-medium text-emerald-600">
          <CircleCheck className="h-3.5 w-3.5" /> Answered
        </p>
      )}
    </section>
  );
};

/* =========================== Progress panel ============================ */

/** Question in view, for highlighting it in the panel. */
const useActiveQuestion = (count: number) => {
  const [active, setActive] = useState(0);
  useEffect(() => {
    const compute = () => {
      let current = 0;
      for (let i = 0; i < count; i++) {
        const el = document.getElementById(`question-${i + 1}`);
        if (el && el.getBoundingClientRect().top <= window.innerHeight * 0.4)
          current = i;
      }
      setActive(current);
    };
    compute();
    window.addEventListener("scroll", compute, { passive: true });
    return () => window.removeEventListener("scroll", compute);
  }, [count]);
  return active;
};

const ProgressRing = ({ value }: { value: number }) => {
  const radius = 26;
  const circumference = 2 * Math.PI * radius;
  return (
    <div className="relative h-16 w-16 shrink-0">
      <svg viewBox="0 0 64 64" className="h-16 w-16 -rotate-90">
        <circle
          cx="32"
          cy="32"
          r={radius}
          fill="none"
          strokeWidth="6"
          className="stroke-gray-100"
        />
        <circle
          cx="32"
          cy="32"
          r={radius}
          fill="none"
          strokeWidth="6"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - value / 100)}
          className="stroke-theme-primary transition-[stroke-dashoffset] duration-500"
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-sm font-semibold tabular-nums text-gray-900">
        {Math.round(value)}%
      </span>
    </div>
  );
};

const ProgressPanel = ({
  answers,
  isSubmitted,
  score,
  totalMarks,
  deadline,
  submitting,
  onCancel,
}: {
  answers: AnswerValue[];
  isSubmitted: boolean;
  score: number | null;
  totalMarks: number;
  deadline?: string;
  submitting: boolean;
  onCancel: () => void;
}) => {
  const active = useActiveQuestion(answers.length);
  const answeredCount = answers.filter(isAnswered).length;
  const remaining = answers.length - answeredCount;
  const hasScore = isSubmitted && score !== null && totalMarks > 0;
  const percent = hasScore
    ? (Number(score) / totalMarks) * 100
    : answers.length
      ? (answeredCount / answers.length) * 100
      : 0;

  return (
    <div className={cn(panelClass, "sticky top-24 overflow-hidden")}>
      {/* Summary */}
      <div className="flex items-center gap-4 p-5">
        <ProgressRing value={percent} />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900">
            {hasScore
              ? "Your score"
              : isSubmitted
                ? "Submitted"
                : "Your progress"}
          </p>
          <p className="mt-0.5 text-[13px] text-gray-500">
            {hasScore ? (
              <>
                <span className="font-semibold text-gray-900">{score}</span> of{" "}
                {totalMarks} marks
              </>
            ) : isSubmitted ? (
              "Awaiting evaluation"
            ) : remaining === 0 ? (
              <span className="font-medium text-emerald-600">
                All questions answered
              </span>
            ) : (
              <>
                <span className="font-semibold text-gray-900">
                  {answeredCount}
                </span>{" "}
                of {answers.length} answered
              </>
            )}
          </p>
        </div>
      </div>

      {/* Questions */}
      <div className="border-t border-gray-100 px-2 py-3">
        <p className="px-3 pb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
          Questions
        </p>
        <ol className="max-h-[40vh] space-y-0.5 overflow-y-auto">
          {answers.map((answer, index) => {
            const done = isAnswered(answer);
            const isActive = index === active;
            return (
              <li key={answer.question_id ?? index}>
                <a
                  href={`#question-${index + 1}`}
                  onClick={(e) => {
                    e.preventDefault();
                    document
                      .getElementById(`question-${index + 1}`)
                      ?.scrollIntoView({ behavior: "smooth" });
                  }}
                  className={cn(
                    "flex items-center gap-3 rounded-lg px-3 py-2 text-[13px] transition-colors",
                    isActive
                      ? "bg-gray-100/80 text-gray-900"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900",
                  )}
                >
                  <span
                    className={cn(
                      "flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold",
                      done
                        ? "bg-theme-primary text-white"
                        : isActive
                          ? "border-[1.5px] border-theme-primary text-theme-primary"
                          : "border border-gray-300 text-gray-400",
                    )}
                  >
                    {done ? (
                      <Check className="h-3 w-3" strokeWidth={3} />
                    ) : (
                      index + 1
                    )}
                  </span>
                  <span className="min-w-0 flex-1 truncate">
                    {answer.title}
                  </span>
                  <span className="shrink-0 text-[11px] tabular-nums text-gray-400">
                    {answer.mark || 0} marks
                  </span>
                </a>
              </li>
            );
          })}
        </ol>
      </div>

      {/* Actions */}
      {!isSubmitted && (
        <div className="space-y-3 border-t border-gray-100 bg-gray-50/60 p-5">
          {deadline && (
            <p className="flex items-center gap-2 text-[13px] text-gray-500">
              <CalendarClock className="h-4 w-4 text-rose-500" />
              Due <span className="font-medium text-gray-900">{deadline}</span>
            </p>
          )}
          <Button
            block
            htmlType="submit"
            loading={submitting}
            className="btn-primary h-11 rounded-full font-medium"
          >
            Submit assessment
          </Button>
          <button
            type="button"
            onClick={onCancel}
            className="w-full text-center text-[13px] font-medium text-gray-500 transition-colors hover:text-gray-900"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

/* ================================ Page ================================= */

const Assessment = () => {
  const { applicationId: jobApplicationId } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const job = (state ?? {}) as JobContext;
  const { verifiedInfo } = useAuth();
  const [form] = Form.useForm();

  const { data, isLoading: isFetchingData } = useGetAssessmentInterviewQuery(
    {
      applicant_id: verifiedInfo?.id as string,
      job_application_id: jobApplicationId as string,
    },
    { skip: !verifiedInfo?.id || !jobApplicationId },
  );

  const [submitAssessment, { isLoading: isSubmitting }] =
    useSubmitAssessmentMutation();

  const assignments = data?.data?.assignments;
  const isSubmitted = !!assignments?.submission;
  const answers: AnswerValue[] | undefined = Form.useWatch("answers", form);
  const questionCount = assignments?.assessment.questions.length ?? 0;
  const totalMarks =
    assignments?.assessment.questions.reduce(
      (sum, q) => sum + (Number(q.mark) || 0),
      0,
    ) ?? 0;

  useEffect(() => {
    if (assignments) {
      const initialAnswers = assignments.assessment.questions.map((q) => {
        const submissionAnswer = assignments.submission?.answers.find(
          (sa) => sa.question_id === q.id,
        );

        return {
          question_id: q.id,
          question_type: q.question_type,
          title: q.title,
          mark: q.mark,
          options: q.options,
          answer_text: submissionAnswer?.answer_text || "",
          answer_attachment: submissionAnswer?.answer_attachment
            ? [submissionAnswer.answer_attachment]
            : [],
          obtained_mark: submissionAnswer?.obtained_mark,
          evaluator_comment: submissionAnswer?.evaluator_comment,
        };
      });
      form.setFieldsValue({ answers: initialAnswers });
    }
  }, [assignments, form]);

  const goBack = () => navigate(PATHS.jobPortal.applications);

  const handleSubmit = async (values: any) => {
    try {
      const payload = {
        applicant_id: verifiedInfo?.id as string,
        assignment_id: assignments?.id as string,
        answers: values.answers.map((ans: any) => {
          const result: any = {
            question_id: ans.question_id,
          };
          if (ans.question_type === "attachment") {
            result.answer_attachment = ans.answer_attachment?.[0] || "";
          } else {
            result.answer_text = ans.answer_text || "";
          }
          return result;
        }),
      };

      const res = await submitAssessment(payload).unwrap();

      if (res.success) {
        toast("success", res.message || "Assessment updated successfully");
        form.resetFields();
        goBack();
      } else {
        toast("error", res.message || "Failed to update assessment");
      }
    } catch (error: any) {
      toast("error", error?.data?.message || "Something went wrong");
    }
  };

  if (isFetchingData) {
    return (
      <WebLayout>
        <div className="flex h-[70vh] items-center justify-center">
          <Spin size="large" />
        </div>
      </WebLayout>
    );
  }

  if (!assignments) {
    return (
      <WebLayout>
        <div className="mx-auto max-w-lg px-4 py-20 sm:py-28">
          <EmptyState
            icon={FileQuestion}
            title="No assessment found"
            description="There are no assessment details for this application yet."
            action={
              <Button
                onClick={goBack}
                className="btn-primary h-9 rounded-full px-5 font-medium"
              >
                Back to applied jobs
              </Button>
            }
          />
        </div>
      </WebLayout>
    );
  }

  const submittedAt = assignments.submission?.submitted_at;

  return (
    <WebLayout>
      {/* ---------- Header ---------- */}
      <header className="relative overflow-hidden border-b border-gray-200 bg-white">
        <div className="pointer-events-none absolute -left-40 -top-40 h-[420px] w-[620px] rounded-full bg-theme-primary/[0.12] blur-[120px]" />
        <div className="relative mx-auto max-w-6xl px-4 py-10 sm:px-6 sm:py-12 lg:px-8">
          <button
            onClick={goBack}
            className="inline-flex items-center gap-1.5 text-sm text-gray-500 transition-colors hover:text-gray-900"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to applied jobs
          </button>

          <div className="mt-6 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-theme-primary/10 px-2.5 py-1 text-xs font-medium text-theme-primary ring-1 ring-inset ring-theme-primary/20">
              <ClipboardList className="h-3.5 w-3.5" />
              Assessment
            </span>
            {isSubmitted && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-600/15">
                <CircleCheck className="h-3.5 w-3.5" />
                Submitted
              </span>
            )}
          </div>
          <h1 className="mt-4 text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
            {assignments.assessment.title}
          </h1>
          {job.job_title && (
            <p className="mt-2 text-[15px] text-gray-500">
              For {job.job_title}
              {job.organization_name && ` at ${job.organization_name}`}
            </p>
          )}

          <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-[13px] text-gray-600">
            <span>
              <span className="font-semibold text-gray-900">
                {questionCount}
              </span>{" "}
              question{questionCount === 1 ? "" : "s"}
            </span>
            <span>
              <span className="font-semibold text-gray-900">{totalMarks}</span>{" "}
              total marks
            </span>
            {isSubmitted && submittedAt ? (
              <span className="inline-flex items-center gap-1.5">
                <CalendarClock className="h-3.5 w-3.5 text-gray-400" />
                Submitted {formatDate(submittedAt, "DD MMM, YYYY")} at{" "}
                {formatDate(submittedAt, "hh:mm A")}
              </span>
            ) : assignments.submission_deadline ? (
              <span className="inline-flex items-center gap-1.5 font-medium text-rose-600">
                <CalendarClock className="h-3.5 w-3.5" />
                Due{" "}
                {formatDate(
                  assignments.submission_deadline,
                  "DD MMM, YYYY",
                )} at {formatTimeString(assignments.submission_time)}
              </span>
            ) : null}
            {isSubmitted && assignments.score !== null && (
              <span>
                Score{" "}
                <span className="font-semibold text-gray-900">
                  {assignments.score}
                </span>{" "}
                / {totalMarks}
              </span>
            )}
          </div>
        </div>
      </header>

      {/* ---------- Body ---------- */}
      <Form
        form={form}
        size="large"
        layout="vertical"
        onFinish={handleSubmit}
        className="mx-auto grid max-w-6xl gap-8 px-4 py-10 sm:px-6 lg:grid-cols-[1fr_280px] lg:gap-10 lg:px-8 lg:py-12 [&_.ant-form-item]:!mb-0"
      >
        <div className="min-w-0 space-y-4">
          {isSubmitted && (
            <div className="flex gap-3 rounded-2xl border border-emerald-600/15 bg-emerald-50/60 p-4 text-sm text-emerald-800">
              <CircleCheck className="mt-0.5 h-4 w-4 shrink-0" />
              You've submitted this assessment. Your answers are shown below and
              can't be changed.
            </div>
          )}

          <Form.List name="answers">
            {(fields) =>
              fields.map((field, index) => (
                <Question
                  key={field.key}
                  field={field}
                  index={index}
                  total={fields.length}
                  readOnly={isSubmitted}
                />
              ))
            }
          </Form.List>

          {/* Mobile actions */}
          {!isSubmitted && (
            <div className="flex gap-2 pt-2 lg:hidden">
              <Button
                onClick={goBack}
                className="h-11 flex-1 rounded-full font-medium"
              >
                Cancel
              </Button>
              <Button
                htmlType="submit"
                loading={isSubmitting}
                className="btn-primary h-11 flex-1 rounded-full font-medium"
              >
                Submit assessment
              </Button>
            </div>
          )}
        </div>

        {/* Progress & navigation */}
        <aside className="hidden lg:block">
          <ProgressPanel
            answers={answers ?? []}
            isSubmitted={isSubmitted}
            score={assignments.score}
            totalMarks={totalMarks}
            deadline={
              !isSubmitted && assignments.submission_deadline
                ? `${formatDate(assignments.submission_deadline, "DD MMM, YYYY")} at ${formatTimeString(assignments.submission_time)}`
                : undefined
            }
            submitting={isSubmitting}
            onCancel={goBack}
          />
        </aside>
      </Form>
    </WebLayout>
  );
};

export default Assessment;
